import React, { useState, useEffect } from 'react';
import { Card, Spin, Row, Col, InputNumber, Input, Select, Button, message, Tag, Space, Table, Alert, Popconfirm, Tabs, Modal } from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined, CarryOutOutlined } from '@ant-design/icons';
import { yuan, calcTemplateRowFreight, matchTemplateRow, ZONE_META } from '../utils/format.js';
import { apiFetch, notifyDataChanged } from '../utils/api.js';

const ROW_FIELDS = [
  { key: 'fee_0_1', label: '0-1公斤' },
  { key: 'fee_1_2', label: '1-2公斤' },
  { key: 'fee_2_3', label: '2-3公斤' },
  { key: 'fee_3_first', label: '3公斤以上首重费' },
  { key: 'fee_3_add', label: '3公斤以上续重费（元/公斤）' },
];

const emptyRow = (region = '一区', id = 0) => ({
  id, region, city: '',
  fee_0_1: null, fee_1_2: null, fee_2_3: null,
  fee_3_first: null, fee_3_add: null,
});

const emptyCourierRows = () => ZONE_META.map((z, i) => emptyRow(z.key, i + 1));

export default function FreightTemplate() {
  const [templates, setTemplates] = useState({});       // { 快递名: [rows] }
  const [activeCourier, setActiveCourier] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newModal, setNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  // 试算器
  const [calcCourier, setCalcCourier] = useState('');
  const [calcRegion, setCalcRegion] = useState('一区');
  const [calcProvince, setCalcProvince] = useState('上海市');
  const [calcWeight, setCalcWeight] = useState(1);

  useEffect(() => {
    apiFetch('/api/freight-templates')
      .then(r => r.json())
      .then(d => {
        const data = d.data || {};
        setTemplates(data);
        const keys = Object.keys(data);
        if (keys.length) { setActiveCourier(keys[0]); setCalcCourier(keys[0]); }
        setLoading(false);
      })
      .catch(() => { setTemplates({}); setLoading(false); });
  }, []);

  const rows = templates[activeCourier] || [];
  const updateRow = (idx, key, value) => {
    setTemplates(prev => ({
      ...prev,
      [activeCourier]: (prev[activeCourier] || []).map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
    }));
  };
  const addRow = () => {
    setTemplates(prev => ({ ...prev, [activeCourier]: [...(prev[activeCourier] || []), emptyRow('一区', Date.now())] }));
  };
  const removeRow = (idx) => {
    setTemplates(prev => ({ ...prev, [activeCourier]: (prev[activeCourier] || []).filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!Object.keys(templates).length) { message.warning('请至少保留一个快递公司模板'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/api/freight-templates', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templates }),
      }).then(r => r.json());
      if (res.success) {
        setTemplates(res.data);
        const keys = Object.keys(res.data);
        if (!keys.includes(activeCourier)) setActiveCourier(keys[0] || '');
        message.success('运费模板已保存，订单已按各快递公司模板重算运费');
        notifyDataChanged(); // 通知所有页面刷新（订单运费/利润/统计已重算）
      } else {
        message.error(res.error || '保存失败');
      }
    } catch {
      message.error('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleNewCourier = () => {
    const name = newName.trim();
    if (!name) { message.warning('请输入快递公司名称'); return; }
    if (templates[name]) { message.warning(`「${name}」模板已存在`); return; }
    setTemplates(prev => ({ ...prev, [name]: emptyCourierRows() }));
    setActiveCourier(name);
    setCalcCourier(name);
    setNewName('');
    setNewModal(false);
  };

  const handleDeleteCourier = (name) => {
    const next = { ...templates };
    delete next[name];
    setTemplates(next);
    if (activeCourier === name) {
      const keys = Object.keys(next);
      setActiveCourier(keys[0] || '');
      setCalcCourier(keys[0] || '');
    }
  };

  // 试算
  const calcRows = templates[calcCourier] || [];
  const calcRow = matchTemplateRow(calcRows, calcRegion, calcProvince);
  const calcFee = calcRow ? calcTemplateRowFreight(calcRow, calcWeight) : null;

  const columns = [
    {
      title: '区域', dataIndex: 'region', width: 110, fixed: 'left',
      render: (v, _, idx) => (
        <Select value={v} style={{ width: 92 }} onChange={val => updateRow(idx, 'region', val)}
          options={ZONE_META.map(z => ({ value: z.key, label: <Tag color={z.color} style={{ marginRight: 0 }}>{z.key}</Tag> }))} />
      ),
    },
    {
      title: '城市（可选，填省份/城市名）', dataIndex: 'city', width: 160,
      render: (v, _, idx) => (
        <Input value={v || ''} placeholder="留空=全区通用" onChange={e => updateRow(idx, 'city', e.target.value)} />
      ),
    },
    ...ROW_FIELDS.slice(0, 3).map(f => ({
      title: f.label + '(元)', key: f.key, width: 110, align: 'right',
      render: (_, r, idx) => (
        <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="未填"
          value={r[f.key] ?? null} onChange={v => updateRow(idx, f.key, v)} />
      ),
    })),
    ...ROW_FIELDS.slice(3).map(f => ({
      title: f.label, key: f.key, width: 150, align: 'right',
      render: (_, r, idx) => (
        <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="未填"
          value={r[f.key] ?? null} onChange={v => updateRow(idx, f.key, v)} />
      ),
    })),
    {
      title: '操作', key: 'op', width: 60, align: 'center', fixed: 'right',
      render: (_, __, idx) => (
        <Popconfirm title="删除该行？" onConfirm={() => removeRow(idx)} okText="删除" cancelText="取消">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;

  const courierKeys = Object.keys(templates);

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>运费模板</div>
          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
            按快递公司分别配置：订单按发货快递自动匹配对应运费模板
          </div>
        </div>
        <Space>
          <Button icon={<PlusOutlined />} type="primary" ghost onClick={() => setNewModal(true)}>新建快递公司模板</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>保存全部模板</Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="计费说明"
        description="订单运费 = 按订单发货快递公司匹配对应模板 → 按区域+订单省份匹配行 → 按订单商品总重量（单件克×数量）查档位（0-1/1-2/2-3KG 固定运费，3KG以上=重量×续重费+首重）。未为该快递配置模板时，回退「默认」模板，再没有则用商品默认运费兜底。「城市」列填省份/城市名（上海市自动匹配上海），匹配不到用城市留空的通用行。"
      />

      {courierKeys.length === 0 ? (
        <Card><div style={{ textAlign: 'center', color: '#8c8c8c', padding: 32 }}>暂无快递模板，点击右上角「新建快递公司模板」开始配置</div></Card>
      ) : (
        <Card
          title={
            <Tabs
              activeKey={activeCourier}
              onChange={setActiveCourier}
              items={courierKeys.map(name => ({
                key: name,
                label: (
                  <Space size={4}>
                    <CarryOutOutlined />
                    {name}
                    {name !== '默认' && (
                      <Popconfirm title={`删除「${name}」模板？`} onConfirm={() => handleDeleteCourier(name)} okText="删除" cancelText="取消">
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={e => e.stopPropagation()} />
                      </Popconfirm>
                    )}
                  </Space>
                ),
              }))}
            />
          }
          extra={<Button icon={<PlusOutlined />} onClick={addRow}>添加一行</Button>}
          style={{ marginBottom: 16 }}
        >
          <Table
            rowKey={(r) => r.id ?? `${r.region}-${r.city}-${Math.random()}`}
            columns={columns}
            dataSource={rows}
            size="small"
            pagination={false}
            scroll={{ x: 1000 }}
            locale={{ emptyText: '暂无模板行，点击右上角「添加一行」开始配置' }}
          />
        </Card>
      )}

      <Card
        title="试算器"
        extra={
          <Space wrap>
            <span style={{ color: '#8c8c8c' }}>快递：</span>
            <Select value={calcCourier} style={{ width: 130 }} onChange={setCalcCourier}
              options={courierKeys.map(c => ({ value: c, label: c }))} />
            <span style={{ color: '#8c8c8c' }}>区域：</span>
            <Select value={calcRegion} style={{ width: 90 }} onChange={setCalcRegion}
              options={ZONE_META.map(z => ({ value: z.key, label: z.key }))} />
            <span style={{ color: '#8c8c8c' }}>省份/城市：</span>
            <Input value={calcProvince} style={{ width: 110 }} placeholder="如 上海" onChange={e => setCalcProvince(e.target.value)} />
            <span style={{ color: '#8c8c8c' }}>重量(kg)：</span>
            <InputNumber min={0.01} precision={2} value={calcWeight} onChange={v => setCalcWeight(v || 0)} style={{ width: 110 }} />
          </Space>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ color: '#8c8c8c' }}>匹配模板行：</span>
          {calcRow ? (
            <Tag color={ZONE_META.find(z => z.key === calcRow.region)?.color}>
              {calcCourier} · {calcRow.region}{calcRow.city ? `-${calcRow.city}` : '（通用）'}
            </Tag>
          ) : <Tag>未配置</Tag>}
          <span style={{ color: '#8c8c8c' }}>运费：</span>
          {calcFee != null
            ? <span style={{ fontSize: 18, fontWeight: 700, color: '#CF1322' }}>¥{yuan(calcFee)}</span>
            : <span style={{ color: '#bfbfbf' }}>该档位未填写 → 回退默认模板 / 商品兜底运费</span>}
        </div>
        <div style={{ marginTop: 12, color: '#8c8c8c', fontSize: 12 }}>
          各档位：0-1KG ¥{calcRow?.fee_0_1 != null ? yuan(calcRow.fee_0_1) : '未填'} · 1-2KG ¥{calcRow?.fee_1_2 != null ? yuan(calcRow.fee_1_2) : '未填'} · 2-3KG ¥{calcRow?.fee_2_3 != null ? yuan(calcRow.fee_2_3) : '未填'} · 3KG+ 首重¥{calcRow?.fee_3_first != null ? yuan(calcRow.fee_3_first) : '未填'} + 重量×¥{calcRow?.fee_3_add != null ? yuan(calcRow.fee_3_add) : '未填'}/kg
        </div>
      </Card>

      <Modal
        title="新建快递公司模板"
        open={newModal}
        onOk={handleNewCourier}
        onCancel={() => setNewModal(false)}
        okText="创建"
        cancelText="取消"
      >
        <p style={{ color: '#8c8c8c', marginTop: 0 }}>输入快递公司名称（如 极兔速递 / 圆通）。订单发货快递匹配到该名称时，将使用此模板计费。</p>
        <Input placeholder="快递公司名称" value={newName} onChange={e => setNewName(e.target.value)} onPressEnter={handleNewCourier} />
      </Modal>
    </div>
  );
}
